import {
  ProjectDetailsFragment,
  WorkspaceMinimalFragment,
} from '@sketch/gql-types'
import { ShowModal } from '@sketch/components'
import {
  CommandBarItem,
  compactCommands,
  createCommand,
} from '@sketch/modules-common'

import { CreateNestedProjectModal, EditProjectModal } from '../modals'
import { ProjectSharingModal } from '../modals/ProjectSharingModal'
import { MoveStorageItemModal } from '../../storageItems'

import { ReactComponent as PermissionsIcon } from '@sketch/icons/persons-2-17'
import { ReactComponent as RenameIcon } from '@sketch/icons/text-edit-17'
import { ReactComponent as SlidersIcon } from '@sketch/icons/sliders-3-17'
import { ReactComponent as MoveIcon } from '@sketch/icons/img-arrows-right-left-17'
import { ReactComponent as CreateFolderIcon } from '@sketch/icons/folder-open-17'
import { ReactComponent as PinIcon } from '@sketch/icons/pushpin-17'

interface CreateCurrentFolderCommandsProps {
  onTogglePin: () => void
  workspace: WorkspaceMinimalFragment
  project: ProjectDetailsFragment
  showModal: ShowModal
}

export const createCurrentFolderCommands = ({
  onTogglePin,
  workspace,
  project,
  showModal,
}: CreateCurrentFolderCommandsProps): CommandBarItem[] => {
  const isDraftFolder = project.projectType === 'PERSONAL_DRAFTS'
  const isTopLevelFolder = !project.parentProjectIdentifier
  const canPinFolder = project.projectType === 'STANDARD' && !project.archivedAt

  return compactCommands([
    canPinFolder &&
      createCommand(
        {
          id: 'folder.toggle-pin',
          analyticsId: project.pinnedByCurrentUserAt
            ? 'folder.unpin'
            : 'folder.pin',
          title: project.pinnedByCurrentUserAt ? 'Unpin Folder' : 'Pin Folder',
          section: 'Current Folder',
          keywords: ['pin', 'pinned'],
          icon: PinIcon,
          priority: 25,
        },
        onTogglePin
      ),
    createCommand(
      {
        id: 'folder.edit-permissions',
        analyticsId: 'folder.edit-permissions',
        title: 'Edit Permissions…',
        section: 'Current Folder',
        disabledReason: isDraftFolder
          ? 'You cannot edit a draft folder’s permissions'
          : undefined,
        keywords: ['permission', 'public'],
        icon: PermissionsIcon,
        priority: 25,
      },
      () =>
        showModal(ProjectSharingModal, {
          project,
          workspace,
        })
    ),
    createCommand(
      {
        id: 'folder.rename',
        analyticsId: 'folder.rename',
        title: 'Rename…',
        section: 'Current Folder',
        disabledReason:
          isDraftFolder && isTopLevelFolder
            ? 'You cannot rename your "My Drafts" folder'
            : undefined,
        keywords: ['rename', 'edit'],
        icon: RenameIcon,
        priority: 25,
      },
      () =>
        showModal(EditProjectModal, {
          project,
          workspaceIdentifier: workspace.identifier,
        })
    ),
    createCommand(
      {
        id: 'folder.edit',
        analyticsId: 'folder.edit',
        title: 'Edit…',
        section: 'Current Folder',
        disabledReason:
          isDraftFolder && isTopLevelFolder
            ? 'You cannot edit your "My Drafts" folder’s settings'
            : undefined,
        keywords: ['edit'],
        icon: SlidersIcon,
        priority: 25,
      },
      () =>
        showModal(EditProjectModal, {
          project,
          workspaceIdentifier: workspace.identifier,
        })
    ),
    createCommand(
      {
        id: 'folder.move',
        analyticsId: 'folder.move',
        title: 'Move to…',
        section: 'Current Folder',
        disabledReason:
          isDraftFolder && isTopLevelFolder
            ? 'You cannot move your "My Drafts" folder'
            : undefined,
        keywords: ['transfer', 'move'],
        icon: MoveIcon,
        priority: 25,
      },
      () =>
        showModal(MoveStorageItemModal, {
          items: [project],
          workspaceIdentifier: workspace.identifier,
          parentProjectIdentifier: project.parentProjectIdentifier || null,
          originType: 'standard',
          action: 'move',
        })
    ),
    createCommand(
      {
        id: 'folder.create-folder',
        analyticsId: 'folder.create-folder',
        title: 'Create a folder…',
        section: 'Current Folder',
        keywords: ['create', 'new'],
        icon: CreateFolderIcon,
        priority: 25,
      },
      () =>
        showModal(CreateNestedProjectModal, {
          project,
          workspaceIdentifier: workspace.identifier,
        })
    ),
  ])
}
